/**
 * Displays today's order-processing statistics as a progress bar and
 * accent-colored stat cards (processed, remaining, time taken, duplicates).
 * @module processesOrderInfoCards
 */

import { SpinningLoader } from "risa-oasis-ui_v2";

/** Props for {@link ProcessesOrderInfoCards}. */
interface ProcessesOrderInfoCardsProps {
  processedOrdersToday?: {
    completion_percentage: number;
    orders_processed: number;
    orders_remaining: number;
    time_taken: string;
    loading: boolean;
    skipped_duplicate_order_ids: string[];
  };
  showLoading?: boolean;
}

interface CardData {
  label: string;
  value: string | number;
  accentColor: string;
  valueColor: string;
}

const ProcessesOrderInfoCards = (props: ProcessesOrderInfoCardsProps) => {
  const { processedOrdersToday } = props;

  const completionPct = processedOrdersToday?.completion_percentage ?? 0;

  const cardData: CardData[] = [
    {
      label: "Orders Processed",
      value: processedOrdersToday?.orders_processed ?? 0,
      accentColor: "bg-[#005D49]",
      valueColor: "text-[#005D49]",
    },
    {
      label: "Orders Remaining",
      value: processedOrdersToday?.orders_remaining ?? 0,
      accentColor: "bg-tertiaryRed-3",
      valueColor: "text-tertiaryRed-3",
    },
    {
      label: "Time Taken",
      value: processedOrdersToday?.time_taken ?? "—",
      accentColor: "bg-secondaryYellow-2",
      valueColor: "text-secondaryYellow-2",
    },
    {
      label: "Skipped Duplicates",
      value: processedOrdersToday?.skipped_duplicate_order_ids?.length ?? 0,
      accentColor: "bg-primaryGray-3",
      valueColor: "text-primaryGray-3",
    },
  ];

  return (
    <div className="flex h-full w-full items-center justify-center">
      {props.showLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <SpinningLoader />
        </div>
      ) : (
        <div className="flex w-11/12 flex-col gap-5 py-4">
          {/* Completion progress hero */}
          <div className="rounded-lg border border-primaryGray-15 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-end justify-between">
              <span className="text-sm font-semibold text-primaryGray-1">
                Completion
              </span>
              <span className="text-2xl font-bold text-[#005D49]">
                {completionPct}%
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-primaryGray-15">
              <div
                className="h-full rounded-full bg-[#005D49] transition-all duration-500"
                style={{ width: `${Math.min(completionPct, 100)}%` }}
              />
            </div>
          </div>

          {/* Stat cards grid */}
          <div className="grid grid-cols-2 gap-3">
            {cardData.map(({ label, value, accentColor, valueColor }) => (
              <div
                key={label}
                className="relative overflow-hidden rounded-lg border border-primaryGray-15 bg-white px-4 py-4 shadow-sm"
              >
                <div
                  className={`absolute left-0 top-0 h-full w-1 ${accentColor}`}
                />
                <div className={`mb-1 text-xl font-bold ${valueColor}`}>
                  {value}
                </div>
                <div className="text-[11px] font-medium text-primaryGray-6">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessesOrderInfoCards;
